'use client';

import { useEffect, useState } from 'react';
import { useTheme } from 'next-themes';
import { Sun, Moon, Monitor } from 'lucide-react';

type ThemeOption = 'light' | 'dark' | 'system';

// NOTE: rendered both inside the app (AppLocaleProvider) and on public pages
// (/c/[slug], /catalog) that have NO next-intl provider — so no useTranslations
// here. Labels are aria/title only; keep them in neutral English.
const OPTIONS: { value: ThemeOption; icon: React.ElementType; label: string }[] = [
  { value: 'light',  icon: Sun,     label: 'Light' },
  { value: 'dark',   icon: Moon,    label: 'Dark' },
  { value: 'system', icon: Monitor, label: 'Auto' },
];

export function ThemeToggle({ collapsed = false }: { collapsed?: boolean }) {
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  if (!mounted) {
    return (
      <div className={`flex items-center ${collapsed ? 'gap-0.5 px-1 py-1' : 'gap-2.5 px-3 py-2'}`}>
        <div className="w-4 h-4 opacity-30" />
        {!collapsed && <span className="text-[13px] text-muted-foreground">Theme</span>}
      </div>
    );
  }

  const current: ThemeOption = (theme as ThemeOption) || 'system';

  if (collapsed) {
    const idx = OPTIONS.findIndex(o => o.value === current);
    const next = OPTIONS[(idx + 1) % OPTIONS.length];
    const { icon: Icon, label } = OPTIONS[idx] ?? OPTIONS[0];
    return (
      <button
        onClick={() => setTheme(next.value)}
        aria-label={label}
        title={label}
        className="w-8 h-8 flex items-center justify-center rounded-[8px] text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
      >
        <Icon size={15} />
      </button>
    );
  }

  const segmented = (
    <div className="flex items-center rounded-[8px] bg-muted p-0.5 gap-0">
      {OPTIONS.map(({ value, icon: Icon, label }) => {
        const active = current === value;
        return (
          <button
            key={value}
            onClick={() => setTheme(value)}
            aria-label={label}
            title={label}
            className={`flex items-center justify-center rounded-[6px] transition-all w-7 h-7 ${
              active
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon size={14} />
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="flex items-center justify-between px-3 py-1.5">
      <span className="text-[13px] text-muted-foreground">Theme</span>
      {segmented}
    </div>
  );
}
